import { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import api from '../../../utils/api'
import { Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { AdminUser } from './types';
import { formatDateTimeInUserTimezone } from '../../../utils/timezone';
import { toast } from 'react-toastify';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #d97706;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  max-width: 500px;
`;

const ActionButton = styled(Link)`
  padding: 8px 16px;
  background: #e5e7eb;
  color: #374151;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  text-decoration: none;
  white-space: nowrap;
  &:hover { background: #d1d5db; color: #374151; }
`;

const SearchSection = styled.div`
  margin-bottom: 24px;
`;

const SearchForm = styled.form`
  display: flex;
  gap: 0;
  max-width: 600px;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-right: none;
  border-radius: 4px 0 0 4px;
  font-size: 14px;
  &:focus { outline: none; border-color: #00a6c2; }
  &::placeholder { color: #9ca3af; }
`;

const SearchButton = styled.button`
  padding: 10px 20px;
  background: #00a6c2;
  color: white;
  border: none;
  border-radius: 0 4px 4px 0;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  &:hover { background: #0094ab; }
  &:disabled { background: #9ca3af; cursor: not-allowed; }
`;

const ResultsSection = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
  background: #f9fafb;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 12px;
  font-size: 14px;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: top;
`;

const UserCell = styled.div`
  display: flex;
  gap: 12px;
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
`;

const PlaceholderAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 14px;
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled(Link)`
  color: #00a6c2;
  text-decoration: none;
  font-weight: 500;
  display: block;
  &:hover { text-decoration: underline; }
`;

const UserEmail = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
`;

const UserActions = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 8px;
`;

const IconButton = styled.button<{ $disabled?: boolean }>`
  width: 28px;
  height: 28px;
  padding: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #374151;
  &:hover {
    background: ${({ $disabled }) => ($disabled ? 'white' : '#f3f4f6')};
  }
`;

const PortfolioLink = styled.a`
  color: #00a6c2;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 4px;
  padding: 16px;
  border-top: 1px solid #e5e7eb;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  padding: 6px 12px;
  border: 1px solid ${({ $active }) => ($active ? '#00a6c2' : '#d1d5db')};
  background: ${({ $active }) => ($active ? '#00a6c2' : 'white')};
  color: ${({ $active }) => ($active ? 'white' : '#374151')};
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  &:hover { background: ${({ $active }) => ($active ? '#0094ab' : '#f3f4f6')}; }
  &:disabled { cursor: not-allowed; opacity: 0.5; }
`;

function AdminUsersPage() {
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch current user for timezone formatting (admin pages don't have CurrentUserContext)
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await api.get('/users/me.json');
        setCurrentUser(response.data);
      } catch (err) {
        console.error('Failed to fetch current user:', err);
      }
    };
    fetchCurrentUser();
  }, []);

  const searchUsers = useCallback(async (query: string, pageNum: number = 1) => {
    if (!query.trim()) return;
    setLoading(true);
    setHasSearched(true);
    try {
      const response = await api.get('/admin2/users.json', { 
        params: { query, page: pageNum } 
      });
      setUsers(response.data.users || response.data || []);
      setTotalPages(response.data.totalPages || 1);
      setPage(pageNum);
    } catch (error) {
      console.error('Failed to search users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    searchUsers(searchQuery, 1);
  };

  const handlePageChange = (newPage: number) => {
    searchUsers(searchQuery, newPage);
  };

  const handleImpersonate = async (userId: string) => {
    try {
      // Follow the server-computed `redirectUrl` (= after_sign_in_path_for,
      // legacy parity → /tasks) instead of hardcoding '/'. See
      // AdminUserEditPage#handleImpersonate for the full rationale.
      const { data } = await api.post(`/admin2/users/${userId}/impersonate`);
      window.location.href = data?.redirectUrl || '/tasks';
    } catch (err) {
      console.error('Failed to impersonate user', err);
      toast.error('Failed to impersonate user');
    }
  };

  const handleRequestPasswordReset = async (userId: string) => {
    if (!window.confirm('Are you sure you want to send a password reset email to this user?')) return;
    try {
      // Backend route is POST /admin2/users/:id/request_password_reset
      await api.post(`/admin2/users/${userId}/request_password_reset.json`);
      toast.success('Password reset email sent');
    } catch (error) {
      toast.error('Failed to send password reset email');
    }
  };

  const handleUnlock = async (user: AdminUser) => {
    if (!user.accessLocked) {
      toast.error('User is not locked');
      return;
    }
    if (!window.confirm('Are you sure you want to unlock this user?')) return;
    try {
      // Backend route is POST /admin2/users/:id/unlock (not PUT)
      await api.post(`/admin2/users/${user.id}/unlock.json`);
      toast.success('User unlocked successfully');
      // Refresh search results
      searchUsers(searchQuery, page);
    } catch (error) {
      toast.error('Failed to unlock user');
    }
  };

  const handleDisable = async (userId: string) => {
    if (!window.confirm('Are you sure you want to disable this user? This action cannot be undone.')) return;
    try {
      await api.put(`/admin2/users/${userId}/disable.json`);
      toast.success('User disabled successfully');
      searchUsers(searchQuery, page);
    } catch (error) {
      toast.error('Failed to disable user');
    }
  };

  const handleDelete = async (user: AdminUser) => {
    const displayName = user.fullName || user.email;
    if (!window.confirm(`Are you sure you want to delete ${displayName}? This action cannot be undone.`)) return;
    try {
      await api.delete(`/admin2/users/${user.id}.json`);
      toast.success(`${displayName} was successfully deleted`);
      searchUsers(searchQuery, page);
    } catch (error: any) {
      toast.error(error.response?.data?.errors?.join(', ') || 'Failed to delete user');
    }
  };

  return (
    <AdminLayout title="Users" pageTitle="Users">
      <PageHeader>
        <PageTitle>Users</PageTitle>
        <ActionButtons>
          <ActionButton to="/admin2/users/talent_trained/new">New trained contributors</ActionButton>
          <ActionButton to="/admin2/users/release_notification">Release notification</ActionButton>
          <ActionButton to="/admin2/users/admins">View admins</ActionButton>
          <ActionButton to="/admin2/users/talent_status/new">Update talent status</ActionButton>
        </ActionButtons>
      </PageHeader>

      <SearchSection>
        <SearchForm onSubmit={handleSearch}>
          <SearchInput
            type="text"
            placeholder="Name, email, email domain (@), organizations, or publications."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <SearchButton type="submit" disabled={loading}>
            Search users
          </SearchButton>
        </SearchForm>
      </SearchSection>

      {loading ? (
        <LoadingState>Searching...</LoadingState>
      ) : hasSearched ? (
        users.length > 0 ? (
          <ResultsSection>
            <Table>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Reporting category</Th>
                  <Th>Portfolio</Th>
                  <Th>Login count</Th>
                  <Th>Last activity</Th>
                  <Th>Talent status</Th>
                  <Th>Talent Score</Th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <Td>
                      <UserCell>
                        {user.avatar ? (
                          <Avatar src={user.avatar} alt={user.fullName || ''} />
                        ) : (
                          <PlaceholderAvatar>👤</PlaceholderAvatar>
                        )}
                        <UserInfo>
                          <UserName to={`/admin2/users/${user.id}/edit`}>
                            {user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A'}
                          </UserName>
                          <UserEmail>{user.email}</UserEmail>
                          <UserActions>
                            <IconButton 
                              as={user.policy?.canEdit !== false ? Link : 'button'}
                              to={user.policy?.canEdit !== false ? `/admin2/users/${user.id}/edit` : undefined}
                              title={user.policy?.canEdit === false ? 'Cannot edit' : 'Edit'}
                              $disabled={user.policy?.canEdit === false}
                            >
                              ✏️
                            </IconButton>
                            <IconButton 
                              title={user.policy?.canImpersonate === false ? 'Cannot impersonate' : 'Impersonate'}
                              onClick={() => user.policy?.canImpersonate !== false && handleImpersonate(user.id)}
                              $disabled={user.policy?.canImpersonate === false}
                            >
                              👤
                            </IconButton>
                            <IconButton 
                              title={user.policy?.canChangePassword === false ? 'Cannot send password reset' : 'Send password reset email'}
                              onClick={() => user.policy?.canChangePassword !== false && handleRequestPasswordReset(user.id)}
                              $disabled={user.policy?.canChangePassword === false}
                            >
                              🔑
                            </IconButton>
                            <IconButton 
                              title={!user.accessLocked ? 'User is not locked' : (user.policy?.canLock === false ? 'Cannot unlock' : 'Unlock')}
                              onClick={() => user.accessLocked && user.policy?.canLock !== false && handleUnlock(user)}
                              $disabled={!user.accessLocked || user.policy?.canLock === false}
                            >
                              🔓
                            </IconButton>
                            <IconButton 
                              title={user.policy?.canDisable === false ? 'Cannot disable' : 'Disable'}
                              onClick={() => user.policy?.canDisable !== false && handleDisable(user.id)}
                              $disabled={user.policy?.canDisable === false}
                            >
                              ✖️
                            </IconButton>
                            <IconButton
                              title={
                                user.policy?.canDelete === false
                                  ? 'You do not have the proper authority level to remove this user.'
                                  : user.policy?.eligibleForDestruction === false
                                    ? 'Users working on active stories cannot be dismissed or deleted until they are removed from those stories or those stories are completed.'
                                    : 'Delete'
                              }
                              onClick={() =>
                                user.policy?.canDelete !== false &&
                                user.policy?.eligibleForDestruction !== false &&
                                handleDelete(user)
                              }
                              $disabled={user.policy?.canDelete === false || user.policy?.eligibleForDestruction === false}
                            >
                              🗑️
                            </IconButton>
                          </UserActions>
                        </UserInfo>
                      </UserCell>
                    </Td>
                    <Td>{user.reportingCategory || ''}</Td>
                    <Td>
                      {user.username ? (
                        <PortfolioLink 
                          href={`https://contently.com/${user.username}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          {user.username}
                        </PortfolioLink>
                      ) : ''}
                    </Td>
                    <Td>{user.signInCount ?? ''}</Td>
                    <Td>
                      {user.lastSignInAt 
                        ? formatDateTimeInUserTimezone(user.lastSignInAt, currentUser, "MMM d, yyyy 'at' h:mmaaa")
                        : ''}
                    </Td>
                    <Td>{user.talentStatus || ''}</Td>
                    <Td>{user.talentScore ?? ''}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {totalPages > 1 && (
              <Pagination>
                {Array.from({ length: Math.min(totalPages, 6) }, (_, i) => i + 1).map((p) => (
                  <PageButton
                    key={p}
                    $active={p === page}
                    onClick={() => handlePageChange(p)}
                  >
                    {p}
                  </PageButton>
                ))}
                {totalPages > 6 && <span>...</span>}
                <PageButton
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages}
                >
                  →
                </PageButton>
                <PageButton
                  onClick={() => handlePageChange(totalPages)}
                  disabled={page >= totalPages}
                >
                  ⇥
                </PageButton>
              </Pagination>
            )}
          </ResultsSection>
        ) : (
          <EmptyState>No users found matching your search.</EmptyState>
        )
      ) : null}
    </AdminLayout>
  );
}

export default AdminUsersPage;
